/**
 * @fileoverview Reporting currency selection step for onboarding.
 * @module features/onboarding/components/steps/CurrencyStep
 */

import { ReactElement, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowRight, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { ScrollReveal, getCurrencyFlag } from '@/shared/components/ui'
import { useActiveOrg } from '@/features/orgs/context/ActiveOrgProvider'
import { getOrgSettings, updateOrgSettings } from '@/features/settings/api/orgSettingsService'
import { OrgIdSchema } from '@/features/orgs/schemas/orgSchemas'

interface CurrencyStepProps {
  /** Callback when currency is saved successfully */
  onComplete: (currency: string) => void
}

/**
 * Third step of onboarding - select reporting currency.
 *
 * @component
 * @example
 * ```tsx
 * <CurrencyStep onComplete={(currency) => {
 *   setCurrency(currency)
 *   completeStep()
 * }} />
 * ```
 */
export function CurrencyStep({ onComplete }: CurrencyStepProps): ReactElement {
  const { t } = useTranslation('features-onboarding')
  const { activeOrgId } = useActiveOrg()
  const queryClient = useQueryClient()
  const [selectedCurrency, setSelectedCurrency] = useState<string | null>(null)

  // Fetch current settings and available currencies
  const settingsQuery = useQuery({
    queryKey: activeOrgId ? ['org', activeOrgId, 'settings'] : ['settings', 'no-org'],
    queryFn: () => getOrgSettings(OrgIdSchema.parse(activeOrgId)),
    enabled: !!activeOrgId,
    staleTime: 5 * 60 * 1000,
  })

  // Derive effective currency: pre-select from server data if user hasn't made a selection
  // This avoids cascading renders from setState in useEffect
  const effectiveCurrency = selectedCurrency ?? settingsQuery.data?.reportingCurrency ?? null

  // Save currency mutation
  const updateMutation = useMutation({
    mutationFn: async (currency: string) => {
      if (!activeOrgId) throw new Error('No active organization')
      return updateOrgSettings(OrgIdSchema.parse(activeOrgId), {
        reportingCurrency: currency,
      })
    },
    onSuccess: async (_, currency) => {
      await queryClient.invalidateQueries({ queryKey: ['org', activeOrgId, 'settings'] })
      onComplete(currency)
    },
    onError: () => {
      toast.error(t('currency.error', 'Failed to save currency'))
    },
  })

  const handleContinue = (): void => {
    if (!effectiveCurrency) return
    updateMutation.mutate(effectiveCurrency)
  }

  const allowedCurrencies = settingsQuery.data?.allowedReportingCurrencies ?? []
  const isLoading = settingsQuery.isLoading
  const isPending = updateMutation.isPending

  return (
    <div className="flex flex-col items-center justify-center max-w-md mx-auto">
      {/* Heading */}
      <ScrollReveal animateOnMount blur delay={0}>
        <h2 className="text-3xl font-bold tracking-tight text-foreground mb-4 text-center">
          {t('currency.title', 'Choose your reporting currency')}
        </h2>
      </ScrollReveal>

      <ScrollReveal animateOnMount blur delay={0.1}>
        <p className="text-muted-foreground mb-8 text-center">
          {t('currency.subtitle', 'All financial data will be converted to this currency')}
        </p>
      </ScrollReveal>

      {/* Currency selector */}
      <ScrollReveal animateOnMount blur delay={0.2} className="w-full">
        <div className="flex w-full items-center gap-3">
          {isLoading ? (
            <Skeleton className="h-10 flex-1" />
          ) : (
            <Select
              value={effectiveCurrency ?? ''}
              onValueChange={setSelectedCurrency}
            >
              <SelectTrigger className="h-10 flex-1">
                <SelectValue placeholder={t('currency.placeholder', 'Select currency')} />
              </SelectTrigger>
              <SelectContent>
                {allowedCurrencies.map(code => (
                  <SelectItem key={code} value={code}>
                    <span className="inline-flex items-center gap-2">
                      <span>{getCurrencyFlag(code)}</span>
                      {code}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <Button
            variant="outline"
            onClick={handleContinue}
            disabled={!effectiveCurrency || isPending || isLoading}
          >
            {isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {t('currency.saving', 'Saving...')}
              </>
            ) : (
              <>
                {t('currency.cta', 'Continue')}
                <ArrowRight className="h-4 w-4 ml-2" />
              </>
            )}
          </Button>
        </div>
      </ScrollReveal>
    </div>
  )
}
